import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'

interface ProductDetailProps {
  id: string
  name: string
  tagline: string
  description: string
  icon?: string
  features: string[]
  externalLink?: string
  screenshots?: string[]
  orientation?: 'portrait' | 'landscape'
}

export const ProductDetailPage: React.FC<ProductDetailProps> = ({
  name,
  tagline,
  features,
  externalLink,
  screenshots,
  orientation = 'portrait',
  icon
}) => {
  const isMobile = useIsMobile()
  const { scrollProgress } = useOptimizedScroll()
  const prefersReducedMotion = React.useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  // Scroll to top
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      {/* Main Content Wrapper */}
      <div 
        className="min-h-screen relative"
        style={{ 
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0 
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})` 
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
            className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
            }}
            animate={
            prefersReducedMotion.current
                ? {}
                : {
                    scale: [1, 1.2, 1],
                    x: [0, 50, 0],
                    y: [0, 30, 0],
                }
            }
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
            className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
            }}
            animate={
            prefersReducedMotion.current
                ? {}
                : {
                    scale: [1, 1.3, 1],
                    x: [0, -50, 0],
                    y: [0, -30, 0],
                }
            }
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
            className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
            style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
            }}
            animate={
            prefersReducedMotion.current
                ? {}
                : {
                    scale: [1, 1.15, 1],
                    x: [0, 30, 0],
                    y: [0, -20, 0],
                }
            }
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* App Store Style Content */}
        <div className="relative pt-48 pb-20 px-4 md:px-8 max-w-7xl mx-auto z-10">
          
          {/* Header Section */}
          <section className="flex flex-col md:flex-row gap-8 mb-12">
             {/* Left: Icon */}
             <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
               className="flex-shrink-0 mx-auto md:mx-0"
             >
                <div 
                  className="w-32 h-32 md:w-44 md:h-44 rounded-[32px] md:rounded-[40px] shadow-2xl overflow-hidden flex items-center justify-center border border-black/5 bg-white"
                >
                    {icon ? (
                      <img 
                        src={icon} 
                        alt={`${name} App Icon`} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white to-[#F5F5F7]">
                        <span style={{ fontSize: '64px' }}>✨</span>
                      </div>
                    )}
                </div>
             </motion.div>

             {/* Right: Info */}
             <div className="flex-grow flex flex-col justify-between text-center md:text-left pt-2">
                <div>
                  <motion.h1
                    initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
                    className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight"
                  >
                    {name}
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
                    className="text-gray-500 text-lg font-medium mb-4"
                  >
                    {tagline}
                  </motion.p>
                </div>

                <motion.div
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.5, delay: 0.3 }}
                   className="flex flex-col md:flex-row items-center gap-6 mt-auto"
                >
                     {externalLink && (
                     <a
                       href={externalLink}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="px-10 py-4 rounded-full bg-black text-white font-bold text-base md:text-lg transition-all transform active:scale-95 hover:bg-gray-800 hover:scale-105 hover:shadow-2xl"
                       style={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)' }}
                     >
                       OPEN
                     </a>
                   )}
                   <div className="flex items-center gap-1 text-gray-400 text-xs font-medium">
                      <span>In-App Purchases</span>
                   </div>
                   <div className="hidden md:block w-px h-8 bg-gray-300 mx-2"></div>
                   <div className="flex flex-col items-center md:items-start text-xs text-gray-500">
                      <div className="flex gap-0.5 text-gray-900 font-bold text-sm">
                         4.9 ★★★★★
                      </div>
                      <span className="text-gray-400">128 Ratings</span>
                   </div>
                   <div className="hidden md:block w-px h-8 bg-gray-300 mx-2"></div>
                   <div className="flex flex-col items-center md:items-start text-xs">
                      <span className="font-bold text-gray-900 text-sm">#1</span>
                      <span className="text-gray-400">Business</span>
                   </div>
                </motion.div>
             </div>
          </section>

          <div className="h-px bg-gray-200 w-full mb-10"></div>

          {/* "Screenshots" / Feature Preview Carousel */}
          <section className="mb-12">
             <h3 className="text-2xl font-bold text-gray-900 mb-4 px-2">Preview</h3>
             <motion.div 
               className="flex gap-4 overflow-x-auto pb-8 snap-x px-2 -mx-2 hide-scrollbar items-start"
               initial={{ opacity: 0, x: 20 }}
               whileInView={{ opacity: 1, x: 0 }}
               viewport={{ once: false }}
               transition={{ duration: 0.6 }}
             >
                {/* Real Screenshots (if provided) */}
                {screenshots && screenshots.length > 0 ? (
                  screenshots.map((src, i) => (
                    <div 
                      key={i} 
                      className={`snap-center flex-shrink-0 rounded-[24px] shadow-xl border border-black/5 bg-white overflow-hidden relative ${
                        orientation === 'landscape' 
                          ? 'w-[85vw] md:w-[800px]' 
                          : 'w-[280px] h-[580px] md:w-[300px] md:h-[620px]'
                      }`}
                    >
                       <img 
                         src={src} 
                         alt={`Screenshot ${i + 1}`} 
                         className={orientation === 'landscape' ? "w-full h-auto block" : "w-full h-full object-cover object-top"}
                       />
                    </div>
                  ))
                ) : (
                  /* Fallback: Simulated Screenshots using Features */
                  features.map((feature, i) => (
                    <div 
                      key={i} 
                      className="snap-center flex-shrink-0 w-[280px] h-[500px] md:w-[300px] md:h-[550px] rounded-[32px] p-8 shadow-xl border border-black/5 bg-white relative overflow-hidden group"
                    >
                        <div className="absolute top-0 left-0 w-full h-8 bg-gray-100 border-b border-gray-200 flex items-center justify-center gap-1.5 ">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                            <div className="w-12 h-1.5 rounded-full bg-gray-200"></div>
                        </div>
                        <div className="mt-8 flex flex-col h-full items-center justify-center text-center">
                            <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center text-3xl mb-6 shadow-sm">
                              {['📅', '⚡', '🤖', '📊'][i % 4]}
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.split(' ').slice(0, 3).join(' ')}</h4>
                            <p className="text-gray-500 font-medium leading-relaxed">{feature}</p>
                        </div>
                    </div>
                  ))
                )}
             </motion.div>
          </section>

          <div className="h-px bg-gray-200 w-full mb-10"></div>

          {/* Description & Info Grid */}
          {/* Description & Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
             {/* Left Col: Description */}
             <motion.div 
               className="md:col-span-2"
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.6 }}
             >
                 <p className="text-gray-800 text-lg leading-relaxed mb-4">
                   {name} brings the power of premium scheduling directly to your recruiting workflow. 
                   Designed for modern teams, it combines beautiful aesthetics with powerful AI-driven logic.
                 </p>
                 <p className="text-gray-800 text-lg leading-relaxed">
                   Automate confirmations, sync seamlessly with Google and iCloud, and present a brand-first booking experience to your candidates.
                 </p>
             </motion.div>

             {/* Right Col: Info Grid */}
             <motion.div
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.6, delay: 0.2 }}
             >
                <h3 className="text-xl font-bold text-gray-900 mb-6">Information</h3>
                <dl className="space-y-0">
                   {[
                     { label: 'Provider', value: 'Mernaki Inc.' },
                     { label: 'Size', value: 'SaaS (Cloud)' },
                     { label: 'Category', value: 'Business' },
                     { label: 'Compatibility', value: 'Web & Desktop' },
                     { label: 'Languages', value: 'English, German' },
                     { label: 'Age Rating', value: '4+' },
                   ].map((item, index) => (
                     <motion.div 
                       key={item.label}
                       className="flex justify-between py-4 border-b border-black/5 hover:bg-black/[0.02] transition-colors rounded-lg px-2 -mx-2 bg-transparent"
                       initial={{ opacity: 0, x: 10 }}
                       whileInView={{ opacity: 1, x: 0 }}
                       viewport={{ once: true }}
                       transition={{ duration: 0.4, delay: 0.3 + (index * 0.1) }}
                     >
                        <dt className="text-gray-500 font-medium">{item.label}</dt>
                        <dd className="text-gray-900 font-semibold">{item.value}</dd>
                     </motion.div>
                   ))}
                </dl>
             </motion.div>
          </div>

        </div>
      </div>

       {/* Footer */}
       {isMobile ? (
         <PageMobileFooter 
           sections={[
             {
               title: 'Connect',
               items: [
                 { label: 'Twitter', to: '#' },
                 { label: 'LinkedIn', to: '#' },
                 { label: 'GitHub', to: '#' }
               ]
             },
             {
               title: 'Legal',
               items: [
                 { label: 'Privacy Policy', to: '/privacy-policy' },
                 { label: 'Terms of Service', to: '/terms' }
               ]
             }
           ]}
         />
      ) : (
        <footer
          className="relative"
          style={{ 
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '80px',
            paddingBottom: '60px',
            marginTop: '-80px'
          }}
      >
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent absolute top-0 left-0" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Header */}
          <div className="text-center mb-12 pb-8 pt-16 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <h3 
              className="text-2xl mb-2"
              style={{ 
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
            >
              Products
            </h3>
            <p 
              className="text-sm"
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Explore our ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            
            {/* Column 1 - Brand */}
            <div>
               <div className="flex items-center gap-3 mb-6">
                 <img 
                   src="/Mernaki_Logo_Pack/transparent/mernaki-128.png" 
                   alt="Mernaki" 
                   className="h-8 w-auto"
                   style={{ filter: 'brightness(0) invert(1)' }}
                 />
                  <h3 className="text-lg" style={{ color: '#FFFFFF', fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive", fontWeight: 400 }}>Mernaki</h3>
               </div>
               <p className="text-gray-400 text-sm leading-relaxed mb-6">
                 The all-in-one workspace for modern professionals. Build, manage, and grow your business with our suite of premium tools.
               </p>
               <div className="flex flex-col gap-2">
                 <div className="flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-green-500"></div>
                   <span className="text-sm text-gray-400">All systems operational</span>
                 </div>
               </div>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3 className="text-white font-semibold mb-6 uppercase tracking-widest text-xs">Company</h3>
              <ul className="space-y-4">
                {[
                  { name: 'About', link: '/about' },
                  { name: 'Partnerships', link: '/partnerships' },
                  { name: 'Contact', link: '/contact' }
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.link} className="text-gray-400 hover:text-white transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Support */}
            <div>
              <h3 className="text-white font-semibold mb-6 uppercase tracking-widest text-xs">Support</h3>
              <ul className="space-y-4">
                {[
                  { name: 'Help Center', link: '/help' },
                  { name: 'Documentation', link: '/docs' },
                  { name: 'API Reference', link: '/developer/api' },
                  { name: 'Status Page', link: '/status' }
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.link} className="text-gray-400 hover:text-white transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

             {/* Column 4 - Legal */}
            <div>
              <h3 className="text-white font-semibold mb-6 uppercase tracking-widest text-xs">Legal</h3>
              <ul className="space-y-4">
                {[
                  { name: 'Privacy Policy', link: '/privacy-policy' },
                  { name: 'Terms of Service', link: '/terms' },
                  { name: 'Cookie Policy', link: '/cookies' },
                  { name: 'Impressum', link: '/impressum' }
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.link} className="text-gray-400 hover:text-white transition-colors text-sm">
                       {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
             
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-end items-center gap-4">
             <p className="text-gray-500 text-sm">
               © {new Date().getFullYear()} Mernaki Inc. All rights reserved.
             </p>
          </div>
        </div>
      </footer>
      )}
    </>
  )
}
